import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, Package, TrendingUp, Bell, Settings, 
  User, LogOut, Search, Filter, BarChart, FileText, Users, Shield,
  ChevronLeft, ChevronRight, Menu, Plus, X, Calendar, MapPin, Building2, CircleDollarSign, Clock, AlertCircle, Grip, PlusIcon, Trash2
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { DashboardCard, LoadItem } from '../components/DashboardComponents';
import { fetchUserLoads } from '../store/slices/loadSlice';
import { logoutUser } from '../store/slices/authSlice';
import api from '../utils/api';
import { isLoadExpired } from '../utils/loadUtils';
import SortFilterControls from '../components/SortFilterControls';
import { fetchNotifications } from '../store/slices/notificationSlice';
import SideNavigation from '../components/SideNavigation';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useScreenSize from '../hooks/useScreenSize';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import ActiveLoadsMap from '../components/ActiveLoadsMap';

// Reusable UI class tokens for cohesive, minimalist styling
const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const selectClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const textareaClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const buttonPrimary = 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const buttonSecondary = 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const iconButton = 'p-2 rounded-md bg-white shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const cardClass = 'bg-white rounded-lg border border-gray-200 shadow-sm';

const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { loads, status, error, pagination } = useSelector((state) => state.load);
  const socket = useSelector((state) => state.websocket.socket);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [greeting, setGreeting] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('date_desc');
  const [filterOption, setFilterOption] = useState('open');
  const [isNewLoadModalOpen, setIsNewLoadModalOpen] = useState(false);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  const [stops, setStops] = useState([
    {
      id: '0',
      type: 'pickup',
      address: '',
      city: '',
      state: '',
      zip: '',
      date: null,
      time: '',
      notes: '',
      sequence: 0,
      business_name: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      contact_instructions: ''
    },
    {
      id: '1',
      type: 'delivery',
      address: '',
      city: '',
      state: '',
      zip: '',
      date: null,
      time: '',
      notes: '',
      sequence: 1,
      business_name: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      contact_instructions: ''
    }
  ]);

  const [newLoad, setNewLoad] = useState({
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: ''
    },
    load_type: '',
    pieces: '',
    description: '',
    special_requirements: ''
  });

  const [stopInputRefs, setStopInputRefs] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStopChange = (index, field, value) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], [field]: value };
    setStops(newStops);
  };

  const initializeAutocomplete = useCallback(() => {
    if (!window.google) {
      console.log("Google Maps not loaded");
      return;
    }

    const options = {
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address', 'geometry'],
      types: ['address']
    };

    // Clear any existing listeners to prevent duplicates
    stopInputRefs.forEach((ref) => {
      if (ref.current && ref.current.googleAutoComplete) {
        window.google.maps.event.clearInstanceListeners(ref.current.googleAutoComplete);
        delete ref.current.googleAutoComplete;
      }
    });

    // Initialize autocomplete for each stop input
    stopInputRefs.forEach((ref, index) => {
      if (!ref.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(ref.current, options);
      ref.current.googleAutoComplete = autocomplete; // Store reference to autocomplete

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (!place.geometry) {
          console.log("No details available for input: '" + place.name + "'");
          return;
        }

        let addressData = {
          address: '',
          city: '',
          state: '',
          zip: ''
        };

        // Parse address components
        place.address_components.forEach(component => {
          const type = component.types[0];
          
          if (type === 'street_number') {
            addressData.address = component.long_name;
          }
          else if (type === 'route') {
            addressData.address += (addressData.address ? ' ' : '') + component.long_name;
          }
          else if (type === 'locality') {
            addressData.city = component.long_name;
          }
          else if (type === 'administrative_area_level_1') {
            addressData.state = component.short_name;
          }
          else if (type === 'postal_code') {
            addressData.zip = component.long_name;
          }
        });

        // If no structured address was found, use formatted_address
        if (!addressData.address && place.formatted_address) {
          const parts = place.formatted_address.split(',');
          addressData.address = parts[0].trim();
        }

        // Update the stop with all fields at once
        const newStops = [...stops];
        newStops[index] = {
          ...newStops[index],
          address: addressData.address,
          city: addressData.city,
          state: addressData.state,
          zip: addressData.zip
        };
        
        setStops(newStops);
      });
    });
  }, [stopInputRefs, stops, setStops]);

  // Update the useEffect to properly handle initialization
  useEffect(() => {
    if (isNewLoadModalOpen) {
      const initializeGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          initializeAutocomplete();
        }
      };

      // Initialize immediately if Google Maps is already loaded
      initializeGoogleMaps();

      // Also listen for the custom event in case Google Maps loads later
      window.addEventListener('google-maps-loaded', initializeGoogleMaps);

      return () => {
        window.removeEventListener('google-maps-loaded', initializeGoogleMaps);
      };
    }
  }, [isNewLoadModalOpen, initializeAutocomplete]);

  // Update the useEffect for stopInputRefs to reinitialize autocomplete when stops change
  useEffect(() => {
    const newRefs = stops.map(() => React.createRef());
    setStopInputRefs(newRefs);
  }, [stops]);

  // Add a new useEffect to reinitialize autocomplete when refs change
  useEffect(() => {
    if (isNewLoadModalOpen && stopInputRefs.length > 0) {
      initializeAutocomplete();
    }
  }, [stopInputRefs, isNewLoadModalOpen, initializeAutocomplete]);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Update the input fields styling to ensure they work with Google Places
  const addressInputStyles = `
    w-full 
    pl-10 
    pr-4 
    py-3 
    rounded-lg 
    border 
    border-gray-300 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-transparent
    bg-white
    z-50
  `;

  const handlePlaceSelect = (autocomplete, type) => {
    const place = autocomplete.getPlace();
    if (!place.geometry) {
      console.log("No details available for input: '" + place.name + "'");
      return;
    }

    const addressComponents = place.address_components;
    let city, state, zip, streetNumber, route;

    for (let component of addressComponents) {
      const componentType = component.types[0];

      switch (componentType) {
        case "locality":
          city = component.long_name;
          break;
        case "administrative_area_level_1":
          state = component.short_name;
          break;
        case "postal_code":
          zip = component.long_name;
          break;
        case "street_number":
          streetNumber = component.long_name;
          break;
        case "route":
          route = component.long_name;
          break;
      }
    }

    const fullAddress = `${streetNumber ? streetNumber + ' ' : ''}${route || ''}`;

    setNewLoad(prevLoad => ({
      ...prevLoad,
      [`${type}_address`]: fullAddress,
      [`${type}_city`]: city || '',
      [`${type}_state`]: state || '',
      [`${type}_zip`]: zip || ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setNewLoad(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setNewLoad(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (date, name) => {
    setNewLoad(prev => ({ ...prev, [name]: date }));
  };

  const handleTimeChange = (e, fieldName) => {
    const { value } = e.target;
    setNewLoad(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Update the reset function to initialize with 2 stops
  const resetFormState = () => {
    // Reset stops to initial state with 2 stops
    setStops([
      {
        id: '0',
        type: 'pickup',
        address: '',
        city: '',
        state: '',
        zip: '',
        date: null,
        time: '',
        notes: '',
        sequence: 0,
        business_name: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        contact_instructions: ''
      },
      {
        id: '1',
        type: 'delivery',
        address: '',
        city: '',
        state: '',
        zip: '',
        date: null,
        time: '',
        notes: '',
        sequence: 1,
        business_name: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        contact_instructions: ''
      }
    ]);

    // Reset load details
    setNewLoad({
      weight: '',
      dimensions: {
        length: '',
        width: '',
        height: ''
      },
      load_type: '',
      pieces: '',
      description: '',
      special_requirements: ''
    });

    // Reset step state
    setCurrentStep(1);
    setCompletedSteps([]);
  };

  // Update the handleSubmit function to account for optional fields
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (currentStep !== 3) {
        return;
    }

    if (stops.length < 2) {
        toast.error('At least two stops (pickup and delivery) are required');
        return;
    }

    // Update validation to make contact info optional
    const missingRequiredFields = stops.some(stop => 
        !stop.address || !stop.city || !stop.state || 
        !stop.zip || !stop.date || !stop.time
    );
    
    if (missingRequiredFields) {
        toast.error('Please fill in all required fields for each stop');
        return;
    }

    // Validate that pieces is provided for non-Full Truckload loads
    if (newLoad.load_type !== 'Full Truckload' && !newLoad.pieces) {
        toast.error('Number of pieces is required for this load type');
        return;
    }

    setIsSubmitting(true);

    const cleanedStops = stops.map((stop, index) => ({
        sequence: index,
        type: stop.type,
        address: stop.address,
        city: stop.city,
        state: stop.state,
        zip: stop.zip,
        date: stop.date?.toISOString().split('T')[0],
        time: stop.time,
        notes: stop.notes || '',
        business_name: stop.business_name || '',
        contact_name: stop.contact_name || '',
        contact_phone: stop.contact_phone || '',
        contact_email: stop.contact_email || '',
        contact_instructions: stop.contact_instructions || ''
    }));

    const formattedLoad = {
        load_type: newLoad.load_type,
        weight: newLoad.weight,
        pieces: newLoad.pieces || null, // Make pieces optional
        description: newLoad.description || '',
        special_requirements: newLoad.special_requirements || '',
        ...(newLoad.load_type !== 'Full Truckload' && {
            dimensions: newLoad.dimensions
        }),
        origin_city: cleanedStops[0].city,
        origin_state: cleanedStops[0].state,
        origin_address: cleanedStops[0].address,
        origin_zip: cleanedStops[0].zip,
        pickup_date: cleanedStops[0].date,
        pickup_time: cleanedStops[0].time,
        destination_city: cleanedStops[cleanedStops.length - 1].city,
        destination_state: cleanedStops[cleanedStops.length - 1].state,
        destination_address: cleanedStops[cleanedStops.length - 1].address,
        destination_zip: cleanedStops[cleanedStops.length - 1].zip,
        delivery_date: cleanedStops[cleanedStops.length - 1].date,
        delivery_time: cleanedStops[cleanedStops.length - 1].time,
        stops: cleanedStops
    };

    try {
        const response = await api.post('/api/loads', formattedLoad);
        if (response.status === 201) {
            resetFormState(); // Reset form state on success
            toast.success('Load created successfully');
            closeNewLoadModal();
            fetchDashboardData();
        }
    } catch (error) {
        console.error('Error creating load:', error);
        toast.error(error.response?.data?.error || 'Failed to create load. Please try again.');
    } finally {
        setIsSubmitting(false);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/api/customer/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) setGreeting('Good morning');
      else if (hour < 18) setGreeting('Good afternoon');
      else setGreeting('Good evening');
    };

    updateGreeting();
    const timer = setInterval(updateGreeting, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Convert frontend sort options to backend parameters
  const getSortParams = (sortOption) => {
    switch (sortOption) {
      case 'date_desc':
        return { sort_by: 'created_at', sort_order: 'desc' };
      case 'date_asc':
        return { sort_by: 'created_at', sort_order: 'asc' };
      case 'weight_desc':
        return { sort_by: 'weight', sort_order: 'desc' };
      case 'weight_asc':
        return { sort_by: 'weight', sort_order: 'asc' };
      case 'id_asc':
        return { sort_by: 'id', sort_order: 'asc' };
      case 'id_desc':
        return { sort_by: 'id', sort_order: 'desc' };
      default:
        return { sort_by: 'created_at', sort_order: 'desc' };
    }
  };

  // Convert frontend filter options to backend status parameter
  const getStatusParam = (filterOption) => {
    if (filterOption === 'all') return 'all';
    if (filterOption === 'resubmitted') return 'all'; // Handle resubmitted separately
    return filterOption;
  };

  // Fetch loads with proper parameters
  const fetchLoads = useCallback(() => {
    const sortParams = getSortParams(sortOption);
    const status = 'open';
    
    dispatch(fetchUserLoads({ 
      page: currentPage, 
      perPage: 10, 
      sortBy: sortParams.sort_by,
      sortOrder: sortParams.sort_order,
      status: status,
      search: activeSearchQuery.trim()
    }));
  }, [dispatch, currentPage, sortOption, filterOption, activeSearchQuery]);

  useEffect(() => {
    fetchLoads();
  }, [fetchLoads]);

  useEffect(() => {
    fetchDashboardData();

    if (socket) {
      // Set up WebSocket event listeners
      socket.on('update_load', fetchDashboardData);
      socket.on('new_bid', fetchDashboardData);
      socket.on('update_bid', fetchDashboardData);

      // Clean up event listeners
      return () => {
        socket.off('update_load', fetchDashboardData);
        socket.off('new_bid', fetchDashboardData);
        socket.off('update_bid', fetchDashboardData);
      };
    }
  }, [socket, fetchDashboardData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSort = (option) => {
    setSortOption(option);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleFilter = (option) => {
    setFilterOption(option);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Client-side filtering for special cases like 'resubmitted'
  const applyClientSideFilter = useCallback((loadsToFilter, filterOption) => {
    if (filterOption === 'resubmitted') {
      return loadsToFilter.filter(load => load.resubmitted === true);
    }
    return loadsToFilter;
  }, []);

  // Get the loads to display with client-side filtering for special cases
  // Exclude resubmitted and expired loads from the customer dashboard view
  // Uses centralized isLoadExpired utility from loadUtils.js which properly
  // parses the ISO8601 pickup_datetime string for accurate timezone handling
  const displayLoads = applyClientSideFilter(loads, filterOption)
    .filter(load => !load?.resubmitted && !isLoadExpired(load));

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setActiveSearchQuery(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setCurrentPage(1);
  };

  const openNewLoadModal = () => {
    setIsNewLoadModalOpen(true);
  };

  const closeNewLoadModal = () => {
    setIsNewLoadModalOpen(false);
    resetFormState(); // Reset form state when modal is closed
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  // Update the step constants
  const STEPS = {
    STOPS: {
      id: 1,
      title: 'Stops & Schedule',
      icon: MapPin,
      description: 'Add pickup and delivery locations'
    },
    CARGO: {
      id: 2,
      title: 'Cargo Details',
      icon: Package,
      description: 'Specify shipment type and dimensions'
    },
    DETAILS: {
      id: 3,
      title: 'Additional Info',
      icon: FileText,
      description: 'Add special requirements'
    }
  };

  // Update the step completion check to account for optional fields
  const isSectionComplete = (step) => {
    switch (step) {
      case 1: // Stops & Schedule
        return stops.length >= 2 && stops.every(stop => 
          stop.type && stop.address && stop.city && stop.state && 
          stop.zip && stop.date && stop.time
        );
      case 2: // Cargo Details
        return Boolean(
          newLoad.load_type && 
          newLoad.weight && 
          (newLoad.load_type === 'Full Truckload' || newLoad.pieces) &&
          (newLoad.load_type === 'Full Truckload' || 
            (newLoad.dimensions.length && 
             newLoad.dimensions.width && 
             newLoad.dimensions.height))
        );
      case 3: // Additional Info
        return true; // All fields in this section are optional
      default:
        return false;
    }
  };

  // Update the effect to check section completion
  useEffect(() => {
    const newCompletedSteps = Object.values(STEPS)
      .filter(step => isSectionComplete(step.id));
    setCompletedSteps(newCompletedSteps);
  }, [newLoad, stops]);

  const addStop = () => {
    const newStop = {
      id: String(stops.length),
      type: stops.length === 0 ? 'pickup' : 'delivery',
      address: '',
      city: '',
      state: '',
      zip: '',
      date: null,
      time: '',
      notes: '',
      sequence: stops.length,
      business_name: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      contact_instructions: ''
    };
    setStops([...stops, newStop]);
  };

  const removeStop = (index) => {
    const newStops = stops.filter((_, i) => i !== index);
    // Resequence remaining stops
    newStops.forEach((stop, i) => {
      stop.sequence = i;
    });
    setStops(newStops);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(stops);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update sequences
    items.forEach((item, index) => {
      item.sequence = index;
    });

    setStops(items);
  };

  // Add new state for mobile stepper
  const [showMobileStepContent, setShowMobileStepContent] = useState(true);
  
  // Add step navigation handlers
  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
      setShowMobileStepContent(false);
      setTimeout(() => setShowMobileStepContent(true), 300);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setShowMobileStepContent(false);
      setTimeout(() => setShowMobileStepContent(true), 300);
    }
  };

  // Update the modal content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Stops</h3>
              <button
                type="button"
                onClick={addStop}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Stop
              </button>
            </div>

            <div className="space-y-4">
              {stops.map((stop, index) => (
                <div
                  key={stop.id}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        stop.type === 'pickup' ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <span className="font-medium">Stop {index + 1}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <select
                        value={stop.type}
                        onChange={(e) => handleStopChange(index, 'type', e.target.value)}
                        className={selectClass}
                      >
                        <option value="pickup">Pickup</option>
                        <option value="delivery">Delivery</option>
                      </select>
                      
                      {stops.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          className="p-1 text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="relative mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Business
                      </label>
                      <select
                        value=""
                        onChange={(e) => handleBusinessSelect(index, e.target.value)}
                        className={selectClass}
                        disabled={isLoadingBusinesses}
                      >
                        <option value="">-- Select a business --</option>
                        {businesses.map(business => (
                          <option key={business.id} value={business.id}>
                            {business.name} {business.usage_count > 0 ? `(Used ${business.usage_count} times)` : ''}
                          </option>
                        ))}
                      </select>
                      {isLoadingBusinesses && (
                        <div className="absolute right-3 top-9">
                          <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Business Name (optional)"
                        value={stop.business_name}
                        onChange={(e) => handleStopChange(index, 'business_name', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    
                    <div className="relative">
                      <input
                        ref={stopInputRefs[index]}
                        type="text"
                        placeholder="Street Address *"
                        value={stop.address}
                        onChange={(e) => handleStopChange(index, 'address', e.target.value)}
                        className={inputClass}
                        required
                      />
                      <div className="absolute right-3 top-2 text-red-500">*</div>
                    </div>
                    
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-6 relative">
                        <input
                          type="text"
                          placeholder="City *"
                          value={stop.city}
                          onChange={(e) => handleStopChange(index, 'city', e.target.value)}
                          className={inputClass}
                          required
                        />
                        <div className="absolute right-3 top-2 text-red-500">*</div>
                      </div>
                      <div className="col-span-3 relative">
                        <input
                          type="text"
                          placeholder="State *"
                          value={stop.state}
                          onChange={(e) => handleStopChange(index, 'state', e.target.value)}
                          className={inputClass}
                          required
                        />
                        <div className="absolute right-3 top-2 text-red-500">*</div>
                      </div>
                      <div className="col-span-3 relative">
                        <input
                          type="text"
                          placeholder="ZIP *"
                          value={stop.zip}
                          onChange={(e) => handleStopChange(index, 'zip', e.target.value)}
                          className={inputClass}
                          required
                        />
                        <div className="absolute right-3 top-2 text-red-500">*</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <DatePicker
                          selected={stop.date}
                          onChange={(date) => handleStopChange(index, 'date', date)}
                          className={inputClass}
                          placeholderText="Select date *"
                          required
                        />
                        <div className="absolute right-3 top-2 text-red-500">*</div>
                      </div>
                      <div className="relative">
                        <input
                          type="time"
                          value={stop.time}
                          onChange={(e) => handleStopChange(index, 'time', e.target.value)}
                          className={inputClass}
                          required
                        />
                        <div className="absolute right-3 top-2 text-red-500">*</div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Contact Information (Optional)</h5>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Contact Name"
                          value={stop.contact_name}
                          onChange={(e) => handleStopChange(index, 'contact_name', e.target.value)}
                          className={inputClass}
                        />
                        <input
                          type="tel"
                          placeholder="Contact Phone"
                          value={stop.contact_phone}
                          onChange={(e) => handleStopChange(index, 'contact_phone', e.target.value)}
                          className={inputClass}
                        />
                        <input
                          type="email"
                          placeholder="Contact Email"
                          value={stop.contact_email}
                          onChange={(e) => handleStopChange(index, 'contact_email', e.target.value)}
                          className={inputClass}
                        />
                        <textarea
                          placeholder="Contact Instructions"
                          value={stop.contact_instructions}
                          onChange={(e) => handleStopChange(index, 'contact_instructions', e.target.value)}
                          className={textareaClass}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium mb-4">Load Type <span className="text-red-500">*</span></h4>
                <div className="grid grid-cols-2 gap-3">
                  {['Full Truckload', 'Less Than Truckload', 'Partial Truckload', 'Other'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleInputChange({ target: { name: 'load_type', value: type } })}
                      className={`p-3 text-sm rounded-lg border ${
                        newLoad.load_type === type
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium mb-4">Cargo Details</h4>
                <div className="space-y-3">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight (lbs) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="weight"
                      value={newLoad.weight}
                      onChange={handleInputChange}
                      placeholder="Enter weight"
                      className={inputClass}
                      required
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Pieces {newLoad.load_type !== 'Full Truckload' && <span className="text-red-500">*</span>}
                      {newLoad.load_type === 'Full Truckload' && <span className="text-gray-500 text-xs ml-1">(optional)</span>}
                    </label>
                    <input
                      type="number"
                      name="pieces"
                      value={newLoad.pieces}
                      onChange={handleInputChange}
                      placeholder="Enter number of pieces"
                      className={inputClass}
                      required={newLoad.load_type !== 'Full Truckload'}
                    />
                  </div>

                  {newLoad.load_type !== 'Full Truckload' && (
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-700">
                        Dimensions <span className="text-red-500">*</span>
                      </h5>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <input
                            type="number"
                            name="dimensions.length"
                            value={newLoad.dimensions.length}
                            onChange={handleInputChange}
                            placeholder="Length *"
                            className={inputClass}
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            name="dimensions.width"
                            value={newLoad.dimensions.width}
                            onChange={handleInputChange}
                            placeholder="Width *"
                            className={inputClass}
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            name="dimensions.height"
                            value={newLoad.dimensions.height}
                            onChange={handleInputChange}
                            placeholder="Height *"
                            className={inputClass}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium mb-4">Load Description <span className="text-gray-500 text-xs ml-1">(optional)</span></h4>
                <textarea
                  name="description"
                  value={newLoad.description}
                  onChange={handleInputChange}
                  placeholder="Describe your shipment..."
                  rows={4}
                  className={textareaClass}
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium mb-4">Special Requirements <span className="text-gray-500 text-xs ml-1">(optional)</span></h4>
                <div className="space-y-3">
                  <textarea
                    name="special_requirements"
                    value={newLoad.special_requirements}
                    onChange={handleInputChange}
                    placeholder="Enter any special requirements or instructions..."
                    rows={4}
                    className={textareaClass}
                  />
                  <div className="text-sm text-gray-500">
                    Include details such as:
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Temperature requirements</li>
                      <li>Special handling instructions</li>
                      <li>Equipment requirements</li>
                      <li>Loading/unloading specifications</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  // Update the form navigation buttons section in the modal
  const renderNavigationButtons = () => {
    return (
      <div className="sticky bottom-0 mt-6 flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={handlePrevStep}
          disabled={isSubmitting}
          className={`${buttonSecondary} flex-1 ${currentStep === 1 ? 'invisible' : ''} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Back
        </button>
        {currentStep === 3 ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`${buttonPrimary} flex-1 ${isSubmitting ? 'opacity-90 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? (
              <>
                <svg 
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating Load...
              </>
            ) : (
              'Create Load'
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleNextStep}
            disabled={isSubmitting}
            className={`${buttonPrimary} flex-1 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Continue
          </button>
        )}
      </div>
    );
  };

  const fetchBusinesses = useCallback(async () => {
    setIsLoadingBusinesses(true);
    try {
      const response = await api.get('/api/customer/businesses');
      setBusinesses(response.data.businesses);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load business list');
    } finally {
      setIsLoadingBusinesses(false);
    }
  }, []);

  useEffect(() => {
    if (isNewLoadModalOpen) {
      fetchBusinesses();
    }
  }, [isNewLoadModalOpen, fetchBusinesses]);

  const handleBusinessSelect = (index, businessId) => {
    const business = businesses.find(b => b.id === parseInt(businessId));
    if (!business) return;
    
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      business_name: business.name,
      business_id: business.id,
      address: business.address || newStops[index].address,
      city: business.city || newStops[index].city,
      state: business.state || newStops[index].state,
      zip: business.zip || newStops[index].zip,
      contact_name: business.contact_name || newStops[index].contact_name,
      contact_phone: business.contact_phone || newStops[index].contact_phone,
      contact_email: business.contact_email || newStops[index].contact_email
    };
    
    setStops(newStops);
  };

  const toggleMapExpand = () => {
    setIsMapExpanded(!isMapExpanded);
  };

  if (status === 'loading' || !dashboardData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="bg-gray-100 min-h-screen flex">
      <SideNavigation 
        isOpen={sidebarOpen} 
        onToggle={toggleSidebar} 
      />

      {/* Main Content - Updated for mobile */}
      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0 px-2' : (sidebarOpen ? 'ml-64' : 'ml-16')
      } ${isMapExpanded ? 'overflow-hidden' : ''}`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4 px-2">
            {(isMobile || isTablet) && (
              <button
                onClick={toggleSidebar}
                className={iconButton}
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 ml-2">
              {greeting}, {user.firstName}!
            </h1>
          </div>
          
          {/* Global Search Bar - Mobile Optimized */}
          <div className="mb-4 sm:mb-6 px-2">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
              <div className="flex-grow relative">
                <input
                  type="text"
                  aria-label="Search loads"
                  placeholder="Search loads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${inputClass} sm:text-base pr-10`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className={`${buttonPrimary} px-3 py-2`}
                aria-label="Search"
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </form>
          </div>

          {/* Dashboard Cards - Streamlined 3-card layout */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6 px-2">
            <DashboardCard icon={Truck} title="Active Shipments" value={dashboardData.active_shipments} />
            <DashboardCard 
              icon={TrendingUp} 
              title="On-Time Delivery" 
              value={`${dashboardData.on_time_delivery}%`} 
              trend={dashboardData.on_time_trend >= 0 ? "up" : "down"} 
              trendValue={`${Math.abs(dashboardData.on_time_trend)}%`} 
            />
            <DashboardCard icon={Package} title="Total Shipments" value={dashboardData.total_shipments} />
          </div>

          {/* Active Loads Map */}
          {!isMapExpanded && (
            <ActiveLoadsMap 
              expanded={isMapExpanded} 
              onToggleExpand={toggleMapExpand} 
            />
          )}

          {/* Recent Shipments Section */}
          <div className={`${cardClass} mb-8`}>
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Shipments
                </h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openNewLoadModal}
                  className={buttonPrimary}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Load
                </motion.button>
              </div>
            </div>

            {/* Sort and Filter Controls - Mobile Optimized */}
            <div className="px-4 py-3 border-b border-gray-200">
              <SortFilterControls
                sortOption={sortOption}
                filterOption={filterOption}
                onSort={handleSort}
                onFilter={handleFilter}
                className="flex flex-col sm:flex-row gap-2 sm:gap-4"
              />
            </div>

            {/* Load List */}
            <div className="px-4 py-5 sm:p-6">
              {displayLoads.length > 0 ? (
                displayLoads.map((load) => (
                  <LoadItem 
                    key={load.id} 
                    load={load} 
                    user={user}
                    showBidButton={false}
                    showViewBidsButton={true}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {activeSearchQuery.trim() ? (
                    <div>
                      <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No loads found matching "{activeSearchQuery}"</p>
                      <button 
                        onClick={handleClearSearch}
                        className="mt-2 text-blue-600 hover:text-blue-800"
                      >
                        Clear search
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No loads found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pagination - Mobile Optimized */}
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex justify-center w-full">
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || status === 'loading'}
                    className={`${iconButton} disabled:opacity-50`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <span className="px-4 py-2 text-sm">
                    {status === 'loading' ? (
                      'Loading...'
                    ) : (
                      `Page ${currentPage} of ${pagination.pages || 1}`
                    )}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= (pagination.pages || 1) || status === 'loading'}
                    className={`${iconButton} disabled:opacity-50`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </nav>
              </div>
            </div>
            
            {/* Search Results Info */}
            {activeSearchQuery.trim() && (
              <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
                <div className="text-center text-sm text-blue-700">
                  {status === 'loading' ? (
                    'Searching...'
                  ) : (
                    `Found ${pagination.total || 0} result${(pagination.total || 0) !== 1 ? 's' : ''} for "${activeSearchQuery}"`
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Expanded Map (rendered outside of main when expanded) */}
      {isMapExpanded && (
        <ActiveLoadsMap 
          expanded={isMapExpanded} 
          onToggleExpand={toggleMapExpand} 
        />
      )}

      {/* New Load Modal - Mobile Optimized */}
      <AnimatePresence>
        {isNewLoadModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-2xl mx-auto sm:my-8 max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <button
                  onClick={handlePrevStep}
                  className={`p-2 ${currentStep === 1 ? 'invisible' : ''}`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h2 className="text-lg font-semibold">
                  {STEPS[Object.keys(STEPS)[currentStep - 1]].title}
                </h2>
                <button onClick={closeNewLoadModal} className="p-2">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="relative h-1 bg-gray-200">
                <div
                  className="absolute h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                />
              </div>

              {/* Modal Content */}
              <div className="p-4">
                <AnimatePresence mode="wait">
                  {showMobileStepContent && renderStepContent()}
                </AnimatePresence>

                {renderNavigationButtons()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <ToastContainer 
        position={isMobile ? "bottom-center" : "top-right"}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default CustomerDashboard;
